import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { getPortada, getLogo } from '../services/api';
import { companyInfo } from '../data/mock';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { MapPin, Mail, Phone, MessageCircle, Loader2 } from 'lucide-react';

const Contact = () => {
  const { t } = useLanguage();
  const [portada, setPortada] = useState(null);
  const [logo, setLogo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [portadaData, logoData] = await Promise.all([
          getPortada('contact'),
          getLogo()
        ]);
        setPortada(portadaData);
        setLogo(logoData);
      } catch (error) {
        console.error('Error loading contact data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleWhatsApp = () => {
    const message = 'Hola, me gustaría obtener más información sobre sus productos.';
    const whatsappUrl = `https://wa.me/${companyInfo.social.whatsapp.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <div className="min-h-screen">
      {/* Hero Grande de Contacto con Logo */}
      {portada && (
        <div 
          className="relative min-h-[60vh] flex items-center justify-center overflow-hidden"
          style={{
            backgroundImage: `url(${portada.imageUrl})`,
            backgroundPosition: 'center center',
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat'
          }}
        >
          {/* Overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/80" />
          
          {/* Content - Logo + Title + Subtitle */}
          <div className="relative z-10 container mx-auto px-4 text-center">
            {/* Logo */}
            {logo && logo.logoUrl && (
              <div className="mb-6">
                <img
                  src={logo.logoUrl}
                  alt={logo.altText}
                  className="h-20 md:h-24 mx-auto object-contain"
                  onError={(e) => e.target.style.display = 'none'}
                />
              </div>
            )}

            {/* NAGOC GROUP, S.R.L */}
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
              NAGOC GROUP, S.R.L
            </h1>

            {/* Subtitle */}
            <p className="text-lg md:text-xl text-white/90 max-w-2xl mx-auto">
              {portada.subtitle}
            </p>
          </div>
        </div>
      )}

      {/* Información de Contacto */}
      <div className="container mx-auto px-4 py-16 max-w-4xl">
        {loading ? (
          <div className="text-center py-20">
            <Loader2 className="h-12 w-12 animate-spin mx-auto text-primary" />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              {/* Phone */}
              <Card>
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                    <Phone className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="font-semibold mb-2">Teléfono</h3>
                  <p className="text-muted-foreground">{companyInfo.phone}</p>
                </CardContent>
              </Card>

              {/* Email */}
              <Card>
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                    <Mail className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="font-semibold mb-2">Email</h3>
                  <p className="text-muted-foreground">{companyInfo.email}</p>
                </CardContent>
              </Card>

              {/* Address */}
              <Card className="md:col-span-2">
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                    <MapPin className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="font-semibold mb-2">Dirección</h3>
                  <p className="text-muted-foreground">{companyInfo.address}</p>
                </CardContent>
              </Card>
            </div>

            {/* WhatsApp CTA */}
            <Card className="bg-primary text-primary-foreground">
              <CardContent className="p-8 text-center">
                <MessageCircle className="h-12 w-12 mx-auto mb-4" />
                <h2 className="text-2xl font-bold mb-2">¿Tienes preguntas?</h2>
                <p className="mb-6 opacity-90">
                  Contáctanos por WhatsApp para atención personalizada
                </p>
                <Button
                  size="lg"
                  variant="secondary"
                  onClick={handleWhatsApp}
                >
                  <MessageCircle className="h-5 w-5 mr-2" />
                  Chatear por WhatsApp
                </Button>
              </CardContent>
            </Card>

            {/* Business Info */}
            <Card className="mt-8">
              <CardContent className="p-6">
                <div className="text-center space-y-2">
                  <h3 className="font-semibold text-lg">NAGOC GROUP, S.R.L</h3>
                  <p className="text-sm text-muted-foreground">RNC: {companyInfo.rnc}</p>
                  <p className="text-sm text-muted-foreground">{companyInfo.website}</p>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default Contact;
